import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { createNoise3D } from 'simplex-noise';

const ThreeHeroBanner = () => {
    const mountRef = useRef(null);

    useEffect(() => {
        const mount = mountRef.current;
        if (!mount) return;

        let scene, camera, renderer, composer, mainGroup;
        let mainSphere, glowPlane, backgroundParticles, nebulaFlash;
        const mouse = new THREE.Vector2(0, 0);
        const targetMouse = new THREE.Vector2(0, 0);
        const raycaster = new THREE.Raycaster();
        let mouse3D = new THREE.Vector3();
        const noise3D = createNoise3D();
        const clock = new THREE.Clock();
        
        let interactionPlane; 
        let targetingLines = [];
        let animationFrameId;
        
        let nebulaState = {
            active: false,
            life: 0,
            maxLife: 60
        };

        const sphereRadius = 2;
        const perspectiveFactor = 0.35; 

        const defaultLineColor = new THREE.Color(0xffffff);
        const highlightLineColor = new THREE.Color(0x39ff14);

        const SYMBOL_CONFIG = {
            pool: [ 'I = I₀ / r²', 'I = I₀e⁻μx', 'HVL = ln(2) / μ', 'D = dE / dm', 'K = dEtr / dm', 'Ḋ = Γ ⋅ A / r²', 'PDD(d) = (D(d)/D(dₘₐₓ))x100%', 'TMR(d,r) = D(d,r)/D(dₘₐₓ,r)', 'BSF = Dₛᵤᵣբₐ𝒸ₑ / Dₐᵢᵣ', 'MU = Dose / (Output x Factors)', 'CI = Vᵣᵢ / Vₜ', 'HI = (D₂% - D₉₈%) / D₅₀%', 'BED = nd(1+d/αβ)', 'EQD2 = D ⋅ (d+α/β)/(2+α/β)', 'S = e⁻(αD+βD²)', 'RBE = Dᵣₑբ / Dₜₑₛₜ', 'OER = Dₕᵧₚₒₓᵢ𝒸 / Dₐₑᵣₒᵦᵢ𝒸' ],
            maxCount: 4,
            spawnInterval: 1000,
            lastSpawn: 0,
            activeSymbols: new Map(),
            minDistance: 3.5
        };

        const vertexShader = `
            varying vec3 vNormal;
            varying vec3 vPosition;
            void main() {
                vNormal = normalize(normalMatrix * normal);
                vPosition = (modelViewMatrix * vec4(position, 1.0)).xyz;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
        `;

        const fragmentShader = `
            uniform vec3 uColor;
            uniform vec3 uMousePos;
            varying vec3 vNormal;
            varying vec3 vPosition;
            void main() {
                float rim = 1.0 - smoothstep(0.0, 1.0, abs(dot(vNormal, normalize(-vPosition))));
                vec3 rimColor = uColor * pow(rim, 2.0) * 2.0;
                float dist = distance(vPosition, uMousePos);
                float proximity = smoothstep(1.5, 0.0, dist);
                vec3 proximityGlow = uColor * proximity * 2.5;
                vec3 finalColor = uColor * 0.1 + rimColor + proximityGlow;
                gl_FragColor = vec4(finalColor, 1.0);
            }
        `;

        const init = () => {
            const canvas = mount.querySelector('#threejs-canvas');
            scene = new THREE.Scene();
            camera = new THREE.PerspectiveCamera(75, mount.clientWidth / mount.clientHeight, 0.1, 1000);
            camera.position.z = 10;

            renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true });
            renderer.setSize(mount.clientWidth, mount.clientHeight);
            renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

            const renderScene = new RenderPass(scene, camera);
            const bloomPass = new UnrealBloomPass(new THREE.Vector2(mount.clientWidth, mount.clientHeight), 0.5, 0.1, 0.0);
            composer = new EffectComposer(renderer);
            composer.addPass(renderScene);
            composer.addPass(bloomPass);

            mainGroup = new THREE.Group();
            mainGroup.position.y = -2.5;
            scene.add(mainGroup);

            createBackgroundParticles();
            createNebulaFlash();
            createGlowPlane();
            createMainSphere();
            createIsodoseLines();
            createTargetingLines();
            createInteractionPlane();

            window.addEventListener('resize', onWindowResize);
            mount.addEventListener('mousemove', onMouseMove);
            mount.addEventListener('mouseout', onMouseOut);
            
            animate(0);
        };

        const createBackgroundParticles = () => {
            const count = 3000;
            const positions = new Float32Array(count * 3);
            for (let i = 0; i < count; i++) {
                positions[i * 3] = (Math.random() - 0.5) * 50;
                positions[i * 3 + 1] = (Math.random() - 0.5) * 50;
                positions[i * 3 + 2] = (Math.random() - 0.5) * 50;
            }
            const geometry = new THREE.BufferGeometry();
            geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
            const particleCanvas = document.createElement('canvas');
            particleCanvas.width = 64; particleCanvas.height = 64;
            const context = particleCanvas.getContext('2d');
            const gradient = context.createRadialGradient(32, 32, 0, 32, 32, 32);
            gradient.addColorStop(0, 'rgba(255,255,255,1)');
            gradient.addColorStop(0.2, 'rgba(255,255,255,0.8)');
            gradient.addColorStop(1, 'rgba(255,255,255,0)');
            context.fillStyle = gradient;
            context.fillRect(0, 0, 64, 64);
            const particleTexture = new THREE.CanvasTexture(particleCanvas);
            const material = new THREE.PointsMaterial({ map: particleTexture, size: 0.1, transparent: true, opacity: 0.4, depthWrite: false });
            backgroundParticles = new THREE.Points(geometry, material);
            scene.add(backgroundParticles);
        };
        
        const createNebulaFlash = () => {
            const canvas = document.createElement('canvas');
            canvas.width = 256; canvas.height = 256;
            const context = canvas.getContext('2d');
            const createGradient = (x, y, radius, color) => {
                const gradient = context.createRadialGradient(x, y, 0, x, y, radius);
                gradient.addColorStop(0, `rgba(${color}, 0.6)`);
                gradient.addColorStop(0.4, `rgba(${color}, 0.2)`);
                gradient.addColorStop(1, `rgba(${color}, 0)`);
                return gradient;
            };
            context.fillStyle = createGradient(128, 128, 128, '57, 255, 20');
            context.fillRect(0, 0, 256, 256);
            context.globalCompositeOperation = 'lighter';
            context.fillStyle = createGradient(80, 100, 100, '0, 255, 255');
            context.fillRect(0, 0, 256, 256);
            context.fillStyle = createGradient(180, 160, 90, '57, 255, 20');
            context.fillRect(0, 0, 256, 256);
            const texture = new THREE.CanvasTexture(canvas);
            const flashMaterial = new THREE.MeshBasicMaterial({ map: texture, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false });
            const flashGeometry = new THREE.PlaneGeometry(8, 8);
            nebulaFlash = new THREE.Mesh(flashGeometry, flashMaterial);
            scene.add(nebulaFlash);
        };

        const createGlowPlane = () => {
            const size = 10;
            const canvas = document.createElement('canvas');
            canvas.width = 256; canvas.height = 256;
            const context = canvas.getContext('2d');
            const gradient = context.createRadialGradient(128, 128, 0, 128, 128, 128);
            gradient.addColorStop(0, 'rgba(0, 255, 255, 0.2)');
            gradient.addColorStop(0.5, 'rgba(0, 255, 255, 0.1)');
            gradient.addColorStop(1, 'rgba(0, 255, 255, 0)');
            context.fillStyle = gradient;
            context.fillRect(0, 0, 256, 256);
            const texture = new THREE.CanvasTexture(canvas);
            const material = new THREE.MeshBasicMaterial({ map: texture, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false });
            const geometry = new THREE.PlaneGeometry(size, size);
            glowPlane = new THREE.Mesh(geometry, material);
            glowPlane.scale.y = perspectiveFactor;
            glowPlane.renderOrder = 0.9;
            mainGroup.add(glowPlane);
        };

        const createMainSphere = () => {
            const geometry = new THREE.SphereGeometry(sphereRadius, 64, 64);
            geometry.userData.originalVertices = Array.from(geometry.attributes.position.array);
            const material = new THREE.ShaderMaterial({ vertexShader, fragmentShader, uniforms: { uColor: { value: new THREE.Color(0x00dede) }, uMousePos: { value: new THREE.Vector3(9999, 9999, 9999) } }, transparent: true, depthWrite: false });
            mainSphere = new THREE.Mesh(geometry, material);
            mainSphere.renderOrder = 1;
            mainGroup.add(mainSphere);
        };
        
        const createIsodoseLines = () => {
            const lineCount = 10;
            const spacing = 0.5;
            for (let i = 0; i < lineCount; i++) {
                const radiusX = sphereRadius + (i + 1) * spacing;
                const radiusY = radiusX * perspectiveFactor;
                const material = new THREE.LineBasicMaterial({ transparent: true, opacity: 0.15, vertexColors: true });
                const createColoredArcGeometry = (curve) => {
                    const points = curve.getPoints(64);
                    const geometry = new THREE.BufferGeometry().setFromPoints(points);
                    const colors = new Float32Array(points.length * 3);
                    for (let j = 0; j < points.length; j++) { defaultLineColor.toArray(colors, j * 3); }
                    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
                    return geometry;
                };
                const backCurve = new THREE.EllipseCurve(0, 0, radiusX, radiusY, 0, Math.PI, false, 0);
                const backGeometry = createColoredArcGeometry(backCurve);
                const backLine = new THREE.Line(backGeometry, material.clone());
                backLine.userData.originalPositions = backGeometry.attributes.position.clone();
                backLine.renderOrder = 0;
                mainGroup.add(backLine);
                const frontCurve = new THREE.EllipseCurve(0, 0, radiusX, radiusY, Math.PI, 2 * Math.PI, false, 0);
                const frontGeometry = createColoredArcGeometry(frontCurve);
                const frontLine = new THREE.Line(frontGeometry, material.clone());
                frontLine.userData.originalPositions = frontGeometry.attributes.position.clone();
                frontLine.renderOrder = 2;
                mainGroup.add(frontLine);
            }
        };

        const createTargetingLines = () => {
            targetingLines.length = 0;
            const material = new THREE.LineBasicMaterial({ color: 0x00ffff, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false });
            const vFov = THREE.MathUtils.degToRad(camera.fov);
            const height = 2 * Math.tan(vFov / 2) * camera.position.z;
            const width = height * camera.aspect;
            const screenEdgeX = width / 2;
            const linesData = [
                [new THREE.Vector3(-screenEdgeX, 0, 0), new THREE.Vector3(-sphereRadius, 0, 0)],
                [new THREE.Vector3(sphereRadius, 0, 0), new THREE.Vector3(screenEdgeX, 0, 0)]
            ];
            const isoLineCount = 10; const isoSpacing = 0.5; const endRadius = sphereRadius + (isoLineCount * isoSpacing) / 2;
            const angles = [ Math.PI / 6, -Math.PI / 6, Math.PI - Math.PI / 6, Math.PI + Math.PI / 6 ];
            angles.forEach(angle => {
                const dir = new THREE.Vector3(Math.cos(angle), Math.sin(angle), 0);
                linesData.push([dir.clone().multiplyScalar(sphereRadius), dir.clone().multiplyScalar(endRadius)]);
            });
            linesData.forEach(points => {
                const geometry = new THREE.BufferGeometry().setFromPoints(points);
                const line = new THREE.Line(geometry, material.clone());
                line.userData = { life: Math.random() * 250, maxLife: 250 + Math.random() * 150 };
                line.renderOrder = 1;
                mainGroup.add(line);
                targetingLines.push(line);
            });
        };
        
        const createTextSprite = (text) => {
            const canvas = document.createElement('canvas'); const context = canvas.getContext('2d'); const fontSize = 48;
            context.font = `italic ${fontSize}px "Cormorant Garamond", serif`;
            const metrics = context.measureText(text);
            canvas.width = metrics.width + 20; canvas.height = fontSize + 20;
            context.font = `italic ${fontSize}px "Cormorant Garamond", serif`;
            context.fillStyle = "rgba(255, 255, 255, 0.7)";
            context.fillText(text, 10, fontSize);
            const texture = new THREE.CanvasTexture(canvas);
            const material = new THREE.SpriteMaterial({ map: texture, transparent: true, opacity: 0 });
            const sprite = new THREE.Sprite(material);
            sprite.scale.set(canvas.width / 120, canvas.height / 120, 1.0);
            return sprite;
        };

        const spawnSymbol = (timestamp) => {
            if (SYMBOL_CONFIG.activeSymbols.size >= SYMBOL_CONFIG.maxCount || timestamp - SYMBOL_CONFIG.lastSpawn < SYMBOL_CONFIG.spawnInterval) return;
            const availableSymbols = SYMBOL_CONFIG.pool.filter(s => ![...SYMBOL_CONFIG.activeSymbols.values()].map(v => v.text).includes(s));
            if (availableSymbols.length === 0) return;
            let positionFound = false; let newPos = new THREE.Vector3(); const maxAttempts = 20;
            for (let i = 0; i < maxAttempts; i++) {
                const lineIndex = Math.floor(Math.random() * 10);
                const radiusX = sphereRadius + (lineIndex + 1) * 0.5; const radiusY = radiusX * perspectiveFactor;
                const angle = Math.random() * Math.PI * 2;
                newPos.set(radiusX * Math.cos(angle), radiusY * Math.sin(angle), 0);
                let tooClose = [...SYMBOL_CONFIG.activeSymbols.values()].some(symbol => symbol.sprite.position.distanceTo(newPos) < SYMBOL_CONFIG.minDistance);
                if (!tooClose) { positionFound = true; break; }
            }
            if (!positionFound) return;
            const text = availableSymbols[Math.floor(Math.random() * availableSymbols.length)];
            const sprite = createTextSprite(text);
            sprite.position.copy(newPos);
            const symbolData = { sprite, text, life: 0, maxLife: 600 + Math.random() * 200, forceFade: false };
            SYMBOL_CONFIG.activeSymbols.set(sprite.uuid, symbolData);
            mainGroup.add(sprite);
            SYMBOL_CONFIG.lastSpawn = timestamp;
        };

        const createInteractionPlane = () => {
            const planeGeometry = new THREE.PlaneGeometry(50, 50);
            const planeMaterial = new THREE.MeshBasicMaterial({ visible: false, side: THREE.DoubleSide });
            interactionPlane = new THREE.Mesh(planeGeometry, planeMaterial);
            interactionPlane.position.copy(mainGroup.position);
            scene.add(interactionPlane);
        };
        
        const onWindowResize = () => {
            if (!mount) return;
            camera.aspect = mount.clientWidth / mount.clientHeight;
            camera.updateProjectionMatrix();
            renderer.setSize(mount.clientWidth, mount.clientHeight);
            composer.setSize(mount.clientWidth, mount.clientHeight);
            mainGroup.children.filter(child => targetingLines.includes(child)).forEach(child => mainGroup.remove(child));
            createTargetingLines();
        };

        const onMouseMove = (event) => {
            const rect = mount.getBoundingClientRect();
            targetMouse.x = ((event.clientX - rect.left) / mount.clientWidth) * 2 - 1;
            targetMouse.y = -((event.clientY - rect.top) / mount.clientHeight) * 2 + 1;
        };

        const onMouseOut = () => {
            targetMouse.x = 0;
            targetMouse.y = 0;
        };

        const animate = (timestamp) => {
            animationFrameId = requestAnimationFrame(animate);
            const elapsedTime = clock.getElapsedTime();
            
            mouse.lerp(targetMouse, 0.05);

            raycaster.setFromCamera(mouse, camera);
            const intersects = raycaster.intersectObject(interactionPlane);

            if (intersects.length > 0) {
                const intersectPoint = intersects[0].point;
                const localPoint = mainGroup.worldToLocal(intersectPoint);
                mouse3D.copy(localPoint);
            } else {
                mouse3D.set(9999, 9999, 9999);
            }
            mainSphere.material.uniforms.uMousePos.value.lerp(mouse3D, 0.1);
            
            const positions = mainSphere.geometry.attributes.position;
            const originalVertices = mainSphere.geometry.userData.originalVertices;
            const distToCenter = mouse3D.length();
            const shrinkProximity = Math.max(0, 1.0 - distToCenter / 4.0);
            const globalShrinkFactor = 1.0 - shrinkProximity * 0.15;
            for (let i = 0; i < positions.count; i++) {
                const i3 = i * 3;
                const ox = originalVertices[i3], oy = originalVertices[i3+1], oz = originalVertices[i3+2];
                const noiseDisplacement = noise3D(ox * 0.5, oy * 0.5, elapsedTime * 0.2) * 0.2;
                const morphedVertex = new THREE.Vector3(ox, oy, oz).multiplyScalar(1 + noiseDisplacement);
                const distToMouse = morphedVertex.distanceTo(mouse3D);
                if (distToMouse < 2.5) {
                    const repelForce = (1 - distToMouse / 2.5) * 0.5;
                    morphedVertex.addScaledVector(morphedVertex.clone().normalize().negate(), repelForce);
                }
                morphedVertex.multiplyScalar(globalShrinkFactor);
                positions.setXYZ(i, morphedVertex.x, morphedVertex.y, morphedVertex.z);
            }
            positions.needsUpdate = true;
            mainSphere.geometry.computeVertexNormals();
            
            const pulse = 1 + Math.sin(elapsedTime * 0.5) * 0.05;
            glowPlane.scale.set(pulse, pulse * perspectiveFactor, 1);
            
            backgroundParticles.position.x = mouse.x * 2;
            backgroundParticles.position.y = mouse.y * 2;

            mainGroup.children.forEach(child => {
                if (child.isLine && child.userData.originalPositions) {
                    const positions = child.geometry.attributes.position;
                    const colors = child.geometry.attributes.color;
                    const originalPositions = child.userData.originalPositions;
                    for (let i = 0; i < positions.count; i++) {
                        const pos = new THREE.Vector3().fromBufferAttribute(originalPositions, i);
                        const dist = pos.distanceTo(mouse3D);
                        let displacement = new THREE.Vector3();
                        if (dist < 2) {
                            const force = (1 - dist / 2) * 0.7;
                            displacement.subVectors(pos, mouse3D).normalize().multiplyScalar(force);
                        }
                        pos.add(displacement);
                        positions.setXYZ(i, pos.x, pos.y, pos.z);
                        const colorProximity = Math.max(0, 1 - dist / 2.0);
                        const currentColor = new THREE.Color();
                        currentColor.lerpColors(defaultLineColor, highlightLineColor, colorProximity);
                        colors.setXYZ(i, currentColor.r, currentColor.g, currentColor.b);
                    }
                    positions.needsUpdate = true;
                    colors.needsUpdate = true;
                }
            });

            const lineCount = 10;
            const spacing = 0.5;
            const outerRadius = sphereRadius + (lineCount * spacing);
            if (distToCenter < outerRadius) {
                const proximity = 1.0 - (distToCenter / outerRadius);
                const chance = THREE.MathUtils.lerp(0.005, 0.04, proximity);
                if (!nebulaState.active && Math.random() < chance) {
                    nebulaState.active = true;
                    nebulaState.life = 0;
                    const particlePositions = backgroundParticles.geometry.attributes.position;
                    const randomIndex = Math.floor(Math.random() * particlePositions.count);
                    nebulaFlash.position.set(particlePositions.getX(randomIndex), particlePositions.getY(randomIndex), particlePositions.getZ(randomIndex));
                    nebulaFlash.scale.setScalar(0.01);
                }
            }
            if(nebulaState.active) {
                nebulaState.life++;
                const progress = nebulaState.life / nebulaState.maxLife;
                const scale = Math.sin(progress * Math.PI);
                nebulaFlash.scale.setScalar(scale * 1.5);
                nebulaFlash.material.opacity = scale * 0.7;
                if (nebulaState.life >= nebulaState.maxLife) {
                    nebulaState.active = false;
                    nebulaFlash.material.opacity = 0;
                }
            }
            
            targetingLines.forEach(line => {
                line.userData.life++;
                const life = line.userData.life;
                const maxLife = line.userData.maxLife;
                const fadeIn = 80; const fadeOutStart = maxLife - 80;
                let opacity = (life < fadeIn) ? (life / fadeIn) * 0.2 : (life > fadeOutStart) ? ((maxLife - life) / 80) * 0.2 : 0.2;
                line.material.opacity = opacity;
                if (life > maxLife) line.userData.life = 0;
            });
            
            spawnSymbol(timestamp);
            
            SYMBOL_CONFIG.activeSymbols.forEach((data, uuid) => {
                data.life++;
                if (data.sprite.position.distanceTo(mouse3D) < 2.5) { data.forceFade = true; }
                if (data.forceFade) {
                    data.sprite.material.opacity -= 0.05;
                } else {
                    const fadeIn = 120, fadeOut = 120, fadeOutStart = data.maxLife - fadeOut;
                    if (data.life < fadeIn) data.sprite.material.opacity = THREE.MathUtils.lerp(0, 0.7, data.life / fadeIn);
                    else if (data.life > fadeOutStart) data.sprite.material.opacity = THREE.MathUtils.lerp(0.7, 0, (data.life - fadeOutStart) / fadeOut);
                    else data.sprite.material.opacity = 0.7;
                }
                if (data.sprite.material.opacity <= 0 || data.life >= data.maxLife) {
                    mainGroup.remove(data.sprite);
                    data.sprite.material.map.dispose();
                    data.sprite.material.dispose();
                    SYMBOL_CONFIG.activeSymbols.delete(uuid);
                }
            });

            composer.render();
        };

        init();

        return () => {
            cancelAnimationFrame(animationFrameId);
            window.removeEventListener('resize', onWindowResize);
            mount.removeEventListener('mousemove', onMouseMove);
            mount.removeEventListener('mouseout', onMouseOut);
            renderer.dispose();
        };
    }, []);

    return (
        <div ref={mountRef} style={{ position: 'relative', width: '100vw', height: '550px', overflow: 'hidden', top: '-50px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <canvas id="threejs-canvas" style={{
                position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0,
                backgroundImage: 'radial-gradient(circle at center, #121212 0%, #050505 55%)'
            }}></canvas>
            <div style={{
                position: 'relative', zIndex: 1, textAlign: 'center', pointerEvents: 'none', paddingTop: '12vh'
            }}>
                <h1 className="text-5xl md:text-7xl lg:text-8xl font-semibold text-white tracking-tighter" style={{ textShadow: '0px 0px 25px rgba(0, 200, 200, 0.2)' }}>
                    <span className="bg-gradient-to-r from-cyan-400 to-green-400 bg-clip-text text-transparent">SABR</span> Calculator
                </h1>
                <p className="mt-4 md:mt-6 text-lg md:text-xl lg:text-2xl text-gray-300 max-w-2xl mx-auto" style={{ textShadow: '0px 0px 25px rgba(0, 200, 200, 0.2)' }}>
                    Precision in Planning. Confidence in Care.
                </p>
            </div>
        </div>
    );
};

export default ThreeHeroBanner;